import { useState, useRef, useEffect } from "react";
import {
  ArrowLeft, Plus, Trash2, Pencil, Download, ChevronLeft, ChevronRight, ChevronDown, Upload, X,
  FileText, Repeat, TrendingUp, Briefcase, Clock, Car, Building2, Pause, Play, Printer, Landmark, Receipt, CreditCard, AlertTriangle, Users,
} from "lucide-react";
import {
  FREQUENCIES, localToday, invoiceTotals, periodRange, computePL, plSeries, mileageDeduction,
  laborCost, hoursFromTimes, weekStart, toCSV, downloadText, fileToLogoDataURL,
} from "./biz.js";
import { tripDeductions, mileageDeductionFor, usesOfficialMileage, hasOfficialMileage, officialRateText, TAX_DATA_CHECKED } from "./taxData.js";
import { COUNTRIES, payrollPlaceholder, taxIdLabel, taxNameHint } from "./countries.js";
import { frequentTrips } from "./mileageTrips.js";
import { addressDistance } from "./routeDistance.js";
import { effectiveStatus, outstandingSummary, invoicePaidAmount, invoiceBalance } from "./biz.js";

const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
export const selStyleExport = { background: "var(--card2)", color: "var(--ivory)", border: "1px solid var(--hair)" };
const selStyle = selStyleExport;
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pretty = (iso) => (iso ? `${MON[+iso.slice(5, 7) - 1]} ${+iso.slice(8, 10)}, ${iso.slice(0, 4)}` : "");
const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v) || 0);

export function Header({ title, onBack, right }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      <button onClick={onBack} className="p-1 -ml-1"><ArrowLeft size={18} className="text-ivory-dim" /></button>
      <h2 className="v-display text-xl font-semibold flex-1">{title}</h2>
      {right}
    </div>
  );
}

export function TaxDisclaimer({ compact }) {
  return (
    <div className="flex items-start gap-2.5 rounded-xl px-3.5 py-3 mb-4 text-xs leading-relaxed" style={{ background: "var(--brass-dim)" }} role="note">
      <AlertTriangle size={15} className="shrink-0 mt-0.5 text-brass" />
      <div><span className="font-semibold">For planning only. This is not tax advice.</span>{compact ? "" : " These are estimates built from what you've entered here and published rates, which change. Confirm with a tax professional or your tax authority before paying or filing."}</div>
    </div>
  );
}

export function Field({ label, children, hint }) {
  return (
    <div>
      <label className="text-xs text-ivory-dim">{label}</label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-xs text-ivory-dim mt-1">{hint}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tiles on the Business tab
// ---------------------------------------------------------------------------
export function BusinessTiles({ active, fmt, setView }) {
  const month = computePL(active, periodRange("month", 0));
  const owed = outstandingSummary(active.invoices);
  const tiles = [
    { id: "bizPL", icon: TrendingUp, title: "Profit & loss", sub: "Month, quarter, year" },
    { id: "taxHelp", icon: Landmark, title: "Tax help", sub: "Quarterly & yearly" },
    { id: "clients", icon: Users, title: "Clients", sub: `${(active.clients || []).length} ${(active.clients || []).length === 1 ? "client" : "clients"}` },
    { id: "invoices", icon: FileText, title: "Invoices", sub: owed.overdueCount ? `${owed.overdueCount} overdue` : `${(active.invoices || []).length} total` },
    { id: "recurringInvoices", icon: Repeat, title: "Recurring invoices", sub: `${(active.recurringInvoices || []).filter((t) => !t.paused).length} active` },
    { id: "bizPayments", icon: CreditCard, title: "Online payments", sub: "Links, viewed, cards" },
    { id: "mileage", icon: Car, title: "Mileage", sub: `${(active.mileage || []).length} trips` },
    { id: "hours", icon: Clock, title: "Staff hours", sub: `${(active.staff || []).length} staff` },
    { id: "payslips", icon: Receipt, title: "Payslips", sub: `${(active.payslips || []).length} issued` },
    { id: "bizDetails", icon: Building2, title: "Details & logo", sub: "Shown on invoices" },
  ];
  return (
    <div className="mb-6">
      <button onClick={() => setView("bizPL")} className="w-full card-hero rounded-2xl p-4 mb-3 text-left">
        <div className="text-xs text-ivory-dim">Net profit this month</div>
        <div className="v-mono text-2xl font-medium mt-0.5" style={{ color: month.profit >= 0 ? "var(--sage)" : "var(--rust)" }}>{fmt(month.profit)}</div>
        <div className="text-xs text-ivory-dim mt-1">{fmt(month.revenue)} in · {fmt(month.expenses)} out</div>
      </button>
      {owed.openCount > 0 && (
        <button onClick={() => setView("invoices")} className="w-full bg-card rounded-2xl p-3.5 mb-3 text-left flex items-center gap-3">
          <FileText size={16} className="text-brass shrink-0" />
          <div className="flex-1 min-w-0"><div className="text-sm font-semibold">{fmt(owed.outstanding)} owed to you</div>
            <div className="text-xs" style={{ color: owed.overdueCount ? "var(--rust)" : "var(--ivory-dim)" }}>{owed.overdueCount ? `${owed.overdueCount} overdue (${fmt(owed.overdue)}). Tap to send reminders.` : `${owed.openCount} open, none overdue`}</div></div>
        </button>)}
      <div className="grid grid-cols-2 gap-2">
        {tiles.map((t) => (
          <button key={t.id} onClick={() => setView(t.id)} className="bg-card rounded-xl p-3.5 text-left flex flex-col gap-1.5">
            <t.icon size={16} className="text-brass" />
            <div className="text-sm font-semibold leading-tight">{t.title}</div>
            <div className="text-xs text-ivory-dim">{t.sub}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Business details & logo
// ---------------------------------------------------------------------------
export function BusinessDetailsView({ active, updateActive, setView, showToast }) {
  const b = active.business || {};
  const [d, setD] = useState({
    name: b.name || "", address: b.address || "", email: b.email || "", phone: b.phone || "", website: b.website || "",
    taxId: b.taxId || "", taxLabel: b.taxLabel || "Tax", taxRate: b.taxRate ?? "", invoicePrefix: b.invoicePrefix || "INV-",
    dueDays: b.dueDays ?? 14, paymentInfo: b.paymentInfo || "", invoiceNotes: b.invoiceNotes || "", logo: b.logo || "",
    activities: (b.activities || []).join("\n"),
  });
  const fileRef = useRef(null);
  const set = (k) => (e) => setD((x) => ({ ...x, [k]: e.target.value }));

  async function onLogo(file) {
    if (!file) return;
    try { setD((x) => ({ ...x, logo: "" })); const url = await fileToLogoDataURL(file); setD((x) => ({ ...x, logo: url })); }
    catch (e) { showToast(e.message); }
  }

  function save() {
    updateActive((p) => ({ ...p, business: { ...(p.business || {}), ...d, taxRate: d.taxRate === "" ? "" : num(d.taxRate), dueDays: num(d.dueDays), activities: [...new Set(String(d.activities || "").split("\n").map((a) => a.trim()).filter(Boolean))].slice(0, 50) } }));
    showToast("Business details saved");
    setView("business");
  }

  const input = "w-full rounded-lg px-3 py-2 text-sm";
  return (
    <div className="px-5 pt-8 pb-28">
      <Header title="Details & logo" onBack={() => setView("business")} />
      <p className="text-ivory-dim text-xs mb-5">Appears at the top of every invoice you print or save as PDF.</p>

      <div className="bg-card rounded-2xl p-4 mb-4">
        <div className="text-xs uppercase tracking-wide text-ivory-dim mb-3">Logo</div>
        {d.logo ? (
          <div className="flex items-center gap-4">
            <div className="rounded-lg p-2" style={{ background: "#fff" }}><img src={d.logo} alt="Logo" style={{ maxHeight: 64, maxWidth: 160 }} /></div>
            <div className="flex flex-col gap-2">
              <button onClick={() => fileRef.current?.click()} className="text-xs text-brass font-medium text-left">Replace</button>
              <button onClick={() => setD((x) => ({ ...x, logo: "" }))} className="text-xs text-ivory-dim text-left">Remove</button>
            </div>
          </div>
        ) : (
          <button onClick={() => fileRef.current?.click()} className="w-full flex flex-col items-center gap-1.5 bg-card2 rounded-xl py-6">
            <Upload size={18} className="text-brass" />
            <span className="text-sm font-medium">Upload your logo</span>
            <span className="text-xs text-ivory-dim">PNG with a transparent background looks best</span>
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { onLogo(e.target.files[0]); e.target.value = ""; }} />
      </div>

      <div className="flex flex-col gap-3 mb-5">
        <Field label="Business name (as shown on invoices)"><input value={d.name} onChange={set("name")} placeholder={active.name} className={input} /></Field>
        <Field label="Address"><textarea value={d.address} onChange={set("address")} rows={3} placeholder={"Street\nCity, State ZIP"} className={input} style={selStyle} /></Field>
        <div className="flex gap-3">
          <div className="flex-1"><Field label="Email"><input value={d.email} onChange={set("email")} type="email" className={input} /></Field></div>
          <div className="flex-1"><Field label="Phone"><input value={d.phone} onChange={set("phone")} className={input} /></Field></div>
        </div>
        <Field label="Website (optional)"><input value={d.website} onChange={set("website")} className={input} /></Field>
        <div className="flex gap-3">
          <div className="flex-1"><Field label={taxIdLabel(active.country)}><input value={d.taxId} onChange={set("taxId")} className={input} /></Field></div>
          <div className="flex-1"><Field label="Invoice prefix"><input value={d.invoicePrefix} onChange={set("invoicePrefix")} className={`${input} v-mono`} /></Field></div>
        </div>
        <div className="flex gap-3">
          <div className="flex-1"><Field label="Tax name"><input value={d.taxLabel} onChange={set("taxLabel")} placeholder={taxNameHint(active.country)} className={input} /></Field></div>
          <div className="flex-1"><Field label="Default tax %"><input value={d.taxRate} onChange={set("taxRate")} type="number" placeholder="0" className={`${input} v-mono`} /></Field></div>
          <div className="flex-1"><Field label="Due in (days)"><input value={d.dueDays} onChange={set("dueDays")} type="number" className={`${input} v-mono`} /></Field></div>
        </div>
        <Field label="Payment instructions"><textarea value={d.paymentInfo} onChange={set("paymentInfo")} rows={3} placeholder="Bank transfer details, Zelle / PayPal address, check payable to…" className={input} style={selStyle} /></Field>
        <Field label="Invoice activities (one per line)"><textarea value={d.activities} onChange={set("activities")} rows={4} placeholder={"Consulting\nDesign\nTravel"} className={input} style={selStyle} /></Field>
        <div className="text-xs text-ivory-dim -mt-1 mb-3">These are suggested in the Activity box when you make an invoice. Activities you've used before are suggested too.</div>
        <Field label="Default invoice note"><textarea value={d.invoiceNotes} onChange={set("invoiceNotes")} rows={2} placeholder="Thank you for your business." className={input} style={selStyle} /></Field>
      </div>
      <button onClick={save} className="w-full bg-gold-grad text-ink font-semibold rounded-full py-3 text-sm">Save</button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Profit & loss
// ---------------------------------------------------------------------------
export function PLView({ active, updateActive, fmt, setView, showToast }) {
  const [kind, setKind] = useState("month");
  const [offset, setOffset] = useState(0);
  const [showIncomeForm, setShowIncomeForm] = useState(false);
  const [incDate, setIncDate] = useState(localToday());
  const [incAmount, setIncAmount] = useState("");
  const [incSource, setIncSource] = useState("");
  const biz = active.business || {};

  const range = periodRange(kind, offset);
  const pl = computePL(active, range);
  const series = plSeries(active, kind, offset, 6);
  const maxV = Math.max(1, ...series.map((s) => Math.max(s.revenue, s.expenses)));
  const profitColor = pl.profit >= 0 ? "var(--sage)" : "var(--rust)";

  function setBizFlag(k, v) { updateActive((p) => ({ ...p, business: { ...(p.business || {}), [k]: v } })); }

  function addIncome() {
    const a = num(incAmount);
    if (a <= 0 || !incDate) return;
    updateActive((p) => ({ ...p, income: [{ id: uid(), date: incDate, amount: a, source: incSource.trim() || "Income", note: "" }, ...(p.income || [])] }));
    setIncAmount(""); setIncSource(""); setShowIncomeForm(false);
    showToast("Income added");
  }
  function deleteIncome(id) { updateActive((p) => ({ ...p, income: (p.income || []).filter((i) => i.id !== id) })); }

  function exportPL() {
    const rows = [["Profit & loss", range.label], [], ["Revenue", ""], ["Paid invoices (excl. tax)", pl.invoiceRevenue.toFixed(2)], ["Other income", pl.otherRevenue.toFixed(2)], ["Total revenue", pl.revenue.toFixed(2)], [], ["Expenses", ""]];
    pl.categoryList.forEach((c) => rows.push([c.name, c.total.toFixed(2)]));
    if (pl.mileage) rows.push(["Mileage deduction", pl.mileage.toFixed(2)]);
    if (pl.wages) rows.push(["Staff wages (payslips)", pl.wages.toFixed(2)]);
    if (pl.employerCosts) rows.push(["Employer payroll costs", pl.employerCosts.toFixed(2)]);
    if (pl.unpaidLabor) rows.push(["Estimated wages for hours not yet on a payslip", pl.unpaidLabor.toFixed(2)]);
    rows.push(["Total expenses", pl.expenses.toFixed(2)], [], ["Net profit", pl.profit.toFixed(2)]);
    downloadText(`profit-loss-${range.label.replace(/\s+/g, "-")}.csv`, toCSV(rows));
  }

  const W = 300, Hh = 96, bw = 15, gap = 8;
  const groupW = bw * 2 + gap;
  const startX = (W - series.length * groupW - (series.length - 1) * 10) / 2;

  return (
    <div className="px-5 pt-8 pb-28 wide-cols">
      <Header title="Profit & loss" onBack={() => setView("business")} right={<button onClick={exportPL} className="flex items-center gap-1 bg-card2 rounded-full px-3 py-1.5 text-xs font-medium"><Download size={12} /> CSV</button>} />

      <div className="grid grid-cols-3 gap-1.5 mb-4 p-1 rounded-xl bg-card">
        {[["month", "Monthly"], ["quarter", "Quarterly"], ["year", "Yearly"]].map(([id, label]) => (
          <button key={id} onClick={() => { setKind(id); setOffset(0); }} className="rounded-lg py-1.5 text-xs font-medium" style={{ background: kind === id ? "var(--brass)" : "transparent", color: kind === id ? "var(--ink)" : "var(--ivory-dim)" }}>{label}</button>
        ))}
      </div>

      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setOffset((o) => o - 1)} className="p-1.5"><ChevronLeft size={18} className="text-ivory-dim" /></button>
        <div className="text-sm font-semibold">{range.label}</div>
        <button onClick={() => setOffset((o) => Math.min(0, o + 1))} disabled={offset >= 0} className="p-1.5 disabled:opacity-25"><ChevronRight size={18} className="text-ivory-dim" /></button>
      </div>

      <div className="card-hero rounded-2xl p-5 mb-4">
        <div className="text-xs uppercase tracking-wide text-ivory-dim">Net profit</div>
        <div className="v-mono text-3xl font-medium mt-1" style={{ color: profitColor }}>{pl.profit < 0 ? "-" : ""}{fmt(Math.abs(pl.profit))}</div>
        <div className="flex gap-6 mt-3 text-xs">
          <div><div className="text-ivory-dim">Revenue</div><div className="v-mono text-sm">{fmt(pl.revenue)}</div></div>
          <div><div className="text-ivory-dim">Expenses</div><div className="v-mono text-sm">{fmt(pl.expenses)}</div></div>
          <div><div className="text-ivory-dim">Margin</div><div className="v-mono text-sm">{pl.margin === null ? "—" : `${pl.margin.toFixed(0)}%`}</div></div>
        </div>
      </div>

      <div className="bg-card rounded-2xl p-4 mb-4">
        <svg viewBox={`0 0 ${W} ${Hh + 18}`} width="100%" role="img" aria-label="Revenue and expenses by period">
          {series.map((s, i) => {
            const x = startX + i * (groupW + 10);
            const rh = (s.revenue / maxV) * Hh, eh = (s.expenses / maxV) * Hh;
            const current = i === series.length - 1;
            return (
              <g key={i} opacity={current ? 1 : 0.7}>
                <rect x={x} y={Hh - rh} width={bw} height={Math.max(rh, s.revenue > 0 ? 1 : 0)} rx={2} style={{ fill: "var(--sage)" }} />
                <rect x={x + bw + gap / 2} y={Hh - eh} width={bw} height={Math.max(eh, s.expenses > 0 ? 1 : 0)} rx={2} style={{ fill: "var(--rust)" }} />
                <text x={x + groupW / 2} y={Hh + 13} textAnchor="middle" fontSize="9" style={{ fill: "var(--ivory-dim)" }}>{s.label}</text>
              </g>
            );
          })}
          <line x1="0" x2={W} y1={Hh} y2={Hh} style={{ stroke: "var(--hair)" }} />
        </svg>
        <div className="flex gap-4 justify-center text-xs text-ivory-dim mt-1">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm" style={{ background: "var(--sage)" }} />Revenue</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm" style={{ background: "var(--rust)" }} />Expenses</span>
        </div>
      </div>

      <div className="bg-card rounded-2xl p-4 mb-4">
        <div className="text-xs uppercase tracking-wide text-ivory-dim mb-2">Revenue</div>
        <div className="flex justify-between py-1.5 text-sm"><span>Paid invoices <span className="text-ivory-dim text-xs">({pl.paidInvoiceCount})</span></span><span className="v-mono">{fmt(pl.invoiceRevenue)}</span></div>
        <div className="flex justify-between py-1.5 text-sm"><span>Other income <span className="text-ivory-dim text-xs">({pl.otherIncome.length})</span></span><span className="v-mono">{fmt(pl.otherRevenue)}</span></div>
        <div className="flex justify-between py-2 text-sm font-semibold border-t border-hair mt-1"><span>Total revenue</span><span className="v-mono">{fmt(pl.revenue)}</span></div>
        <div className="text-xs text-ivory-dim mt-1">Invoices count on the day you mark them Paid, without sales tax.</div>

        <button onClick={() => setShowIncomeForm((v) => !v)} className="flex items-center gap-1 text-xs text-brass font-medium mt-3"><Plus size={13} /> Add other income (cash sales, deposits…)</button>
        {showIncomeForm && (
          <div className="mt-3 flex flex-col gap-2">
            <div className="flex gap-2">
              <input value={incDate} onChange={(e) => setIncDate(e.target.value)} type="date" className="flex-1 rounded-lg px-3 py-2 text-sm" />
              <input value={incAmount} onChange={(e) => setIncAmount(e.target.value)} type="number" placeholder="Amount" className="v-mono w-28 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="flex gap-2">
              <input value={incSource} onChange={(e) => setIncSource(e.target.value)} placeholder="Source, e.g. Market stall" className="flex-1 rounded-lg px-3 py-2 text-sm" />
              <button onClick={addIncome} className="bg-brass text-ink font-semibold rounded-lg px-4 text-sm">Add</button>
            </div>
          </div>
        )}
        {pl.otherIncome.length > 0 && (
          <div className="mt-3">
            {pl.otherIncome.map((i) => (
              <div key={i.id} className="flex items-center gap-2 py-1.5 border-t border-hair">
                <div className="flex-1 min-w-0"><div className="text-sm truncate">{i.source}</div><div className="text-xs text-ivory-dim">{pretty(i.date)}</div></div>
                <div className="v-mono text-sm">{fmt(i.amount)}</div>
                <button onClick={() => deleteIncome(i.id)} className="opacity-40"><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-card rounded-2xl p-4 mb-4">
        <div className="text-xs uppercase tracking-wide text-ivory-dim mb-2">Expenses</div>
        {pl.categoryList.length === 0 && pl.mileage === 0 && pl.labor === 0 && <div className="text-sm text-ivory-dim py-2">No expenses in this period.</div>}
        {pl.categoryList.map((c) => (
          <div key={c.name} className="py-1.5">
            <div className="flex justify-between text-sm"><span>{c.name}</span><span className="v-mono">{fmt(c.total)}</span></div>
            <div className="h-1 rounded-full mt-1" style={{ background: "var(--card2)" }}><div className="h-1 rounded-full" style={{ width: `${Math.max(3, (c.total / Math.max(pl.expenseTotal, 1)) * 100)}%`, background: "var(--brass)" }} /></div>
          </div>
        ))}
        {pl.mileage > 0 && <div className="flex justify-between py-1.5 text-sm"><span>Mileage deduction</span><span className="v-mono">{fmt(pl.mileage)}</span></div>}
        {pl.wages > 0 && <div className="flex justify-between py-1.5 text-sm"><span>Staff wages <span className="text-ivory-dim text-xs">({pl.payslipCount} payslip{pl.payslipCount === 1 ? "" : "s"})</span></span><span className="v-mono">{fmt(pl.wages)}</span></div>}
        {pl.employerCosts > 0 && <div className="flex justify-between py-1.5 text-sm"><span>Employer payroll costs</span><span className="v-mono">{fmt(pl.employerCosts)}</span></div>}
        {pl.unpaidLabor > 0 && <div className="flex justify-between py-1.5 text-sm"><span>Wages estimate <span className="text-ivory-dim text-xs">(hours not on a payslip)</span></span><span className="v-mono">{fmt(pl.unpaidLabor)}</span></div>}
        <div className="flex justify-between py-2 text-sm font-semibold border-t border-hair mt-1"><span>Total expenses</span><span className="v-mono">{fmt(pl.expenses)}</span></div>
        {pl.excludedCount > 0 && <div className="text-xs text-ivory-dim mt-1">{pl.excludedCount} {pl.excludedCount === 1 ? "entry" : "entries"} marked not deductible {pl.excludedCount === 1 ? "is" : "are"} left out.</div>}
      </div>

      <div className="bg-card rounded-2xl p-4 mb-3">
        <div className="text-xs uppercase tracking-wide text-ivory-dim mb-3">What to include</div>
        <label className="flex items-start gap-3 mb-3 text-sm"><input type="checkbox" className="mt-1" checked={biz.plIncludeMileage !== false} onChange={(e) => setBizFlag("plIncludeMileage", e.target.checked)} /><span>Mileage deduction<span className="block text-xs text-ivory-dim">Logged trips × your rate per {biz.distanceUnit || "mi"}.</span></span></label>
        <label className="flex items-start gap-3 mb-3 text-sm"><input type="checkbox" className="mt-1" checked={biz.plIncludePayslips !== false} onChange={(e) => setBizFlag("plIncludePayslips", e.target.checked)} /><span>Wages from payslips<span className="block text-xs text-ivory-dim">Gross pay plus employer costs, counted in the month each payslip is issued. Turn off if you already log wages as expenses.</span></span></label>
        <label className="flex items-start gap-3 text-sm"><input type="checkbox" className="mt-1" checked={!!biz.plIncludeLabor} onChange={(e) => setBizFlag("plIncludeLabor", e.target.checked)} /><span>Estimate wages for hours not yet on a payslip<span className="block text-xs text-ivory-dim">Logged hours times pay rate. Hours already on a payslip are never counted twice.</span></span></label>
      </div>
      <p className="text-xs text-ivory-dim">A simple cash-basis summary for your own planning — not a substitute for an accountant's statements or tax advice.</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mileage
// ---------------------------------------------------------------------------
export function MileageView({ active, updateActive, setView, showToast, fmt }) {
  const biz = active.business || {};
  const unit = biz.distanceUnit || "mi";
  const irs = usesOfficialMileage(biz, active.currency, active.country);
  const canIrs = hasOfficialMileage(active.country, active.currency);
  const rate = biz.mileageRate ?? 0.7;
  const dedMap = tripDeductions(active.mileage || [], biz, active.currency, active.country);
  const officialName = { US: "IRS", UK: "HMRC", AU: "ATO" }[active.country || ((active.currency || "USD") === "USD" ? "US" : "")] || "Official";
  const blank = { date: localToday(), from: "", to: "", purpose: "", distance: "", roundTrip: false, clientId: "" };
  const [f, setF] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [look, setLook] = useState({ busy: false, note: "", err: "" });
  const [year, setYear] = useState(new Date().getFullYear());
  const trips = (active.mileage || []).slice().sort((a, b) => (a.date < b.date ? 1 : -1));
  const yearTrips = trips.filter((t) => t.date.startsWith(String(year)));
  const totalDist = yearTrips.reduce((s, t) => s + t.distance, 0);

  function setBiz(k, v) { updateActive((p) => ({ ...p, business: { ...(p.business || {}), [k]: v } })); }

  // Looks the distance up on its own once both addresses have been typed and left alone for a moment, so people don't have to
  // press the button. It never overwrites a distance the person typed themselves, tries each pair of addresses only once,
  // and repeat trips come from the on-device cache (no lookup used).
  const auto = useRef({ key: "", value: "" });
  useEffect(() => {
    if (editingId) return;
    const a = f.from.trim(), b = f.to.trim();
    if (a.length < 8 || b.length < 8) return;
    const key = `${a}|${b}|${unit}`;
    if (auto.current.key === key) return;
    if (f.distance && f.distance !== auto.current.value) return;      // they entered a distance themselves
    const t = setTimeout(() => { auto.current.key = key; findDistance(); }, 1500);
    return () => clearTimeout(t);
  }, [f.from, f.to, editingId, unit]);

  async function findDistance() {
    setLook({ busy: true, note: "", err: "" });
    try {
      const r = await addressDistance({ from: f.from, to: f.to, country: active.country, unit });
      auto.current.value = String(r.distance);
      setF((cur) => ({ ...cur, distance: String(r.distance) }));
      setLook({ busy: false, note: r.cached ? `${r.distance} ${unit} (saved from an earlier lookup)` : `${r.distance} ${unit} by road${r.fromLabel ? `. ${r.fromLabel} to ${r.toLabel}` : ""}`, err: "" });
    } catch (e) { setLook({ busy: false, note: "", err: e.message || "Couldn't look that up." }); }
  }

  function save() {
    const base = num(f.distance);
    if (base <= 0 || !f.date) { showToast("Enter a distance"); return; }
    const entry = { id: editingId || uid(), date: f.date, from: f.from.trim(), to: f.to.trim(), purpose: f.purpose.trim(), distance: f.roundTrip ? base * 2 : base, clientId: f.clientId || null };
    updateActive((p) => ({ ...p, mileage: editingId ? (p.mileage || []).map((t) => (t.id === editingId ? entry : t)) : [entry, ...(p.mileage || [])] }));
    setF({ ...blank, date: f.date }); setEditingId(null);
    showToast(editingId ? "Trip updated" : "Trip logged");
  }

  function startEdit(t) { setEditingId(t.id); setF({ date: t.date, from: t.from || "", to: t.to || "", purpose: t.purpose || "", distance: String(t.distance), roundTrip: false, clientId: t.clientId || "" }); window.scrollTo?.({ top: 0, behavior: "smooth" }); }
  function remove(id) { updateActive((p) => ({ ...p, mileage: (p.mileage || []).filter((t) => t.id !== id) })); if (editingId === id) { setEditingId(null); setF(blank); } }

  function exportCSV() {
    const rows = [["Date", "From", "To", "Purpose", "Client", `Distance (${unit})`, "Rate", "Deduction"]];
    yearTrips.slice().reverse().forEach((t) => rows.push([t.date, t.from, t.to, t.purpose, active.clients.find((c) => c.id === t.clientId)?.name || "", t.distance, (dedMap.get(t.id) / (t.distance || 1)).toFixed(4), (dedMap.get(t.id) || 0).toFixed(2)]));
    rows.push(["", "", "", "", "Total", totalDist, "", mileageDeductionFor(active.mileage || [], biz, active.currency, active.country, { start: `${year}-01-01`, end: `${year}-12-31` }).toFixed(2)]);
    downloadText(`mileage-${year}.csv`, toCSV(rows));
  }

  const years = [...new Set([new Date().getFullYear(), ...trips.map((t) => +t.date.slice(0, 4))])].sort((a, b) => b - a);

  return (
    <div className="px-5 pt-8 pb-28 wide-cols">
      <Header title="Mileage" onBack={() => setView("business")} right={<button onClick={exportCSV} className="flex items-center gap-1 bg-card2 rounded-full px-3 py-1.5 text-xs font-medium"><Download size={12} /> CSV</button>} />

      <div className="card-hero rounded-2xl p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="text-xs text-ivory-dim">Deduction for</div>
          <select value={year} onChange={(e) => setYear(+e.target.value)} className="rounded-md px-2 py-0.5 text-xs" style={selStyle}>{years.map((y) => <option key={y} value={y}>{y}</option>)}</select>
        </div>
        <div className="v-mono text-2xl font-medium mt-1">{fmt(mileageDeductionFor(active.mileage || [], biz, active.currency, active.country, { start: `${year}-01-01`, end: `${year}-12-31` }))}</div>
        <div className="text-xs text-ivory-dim mt-0.5">{totalDist.toLocaleString(undefined, { maximumFractionDigits: 1 })} {unit} across {yearTrips.length} {yearTrips.length === 1 ? "trip" : "trips"}</div>
      </div>

      <div className="bg-card rounded-2xl p-4 mb-4">
        <div className="text-xs uppercase tracking-wide text-ivory-dim mb-3">Rate</div>
        {canIrs && (
          <div className="grid grid-cols-2 gap-1.5 mb-3">
            {[["official", `${officialName} official rate`], ["custom", "My own rate"]].map(([id, label]) => <button key={id} onClick={() => setBiz("mileageMode", id)} className="rounded-lg py-2 text-xs font-medium" style={{ background: (irs ? "official" : "custom") === id ? "var(--brass)" : "var(--card2)", color: (irs ? "official" : "custom") === id ? "var(--ink)" : "var(--ivory-dim)" }}>{label}</button>)}
          </div>)}
        {irs ? (
          <div className="text-xs text-ivory-dim leading-relaxed">Trips are valued at the {officialName} rate: <span className="text-ivory">{officialRateText(active.country, active.currency, localToday())}</span>. {(active.country || "US") === "US" ? "The 2026 rate changed mid-year (72.5¢ to 76¢ from July 1), and that is handled by trip date. " : ""}Rates last checked {TAX_DATA_CHECKED}.</div>
        ) : (<>
          <div className="flex gap-2 items-center">
            <input value={biz.mileageRate ?? 0.7} onChange={(e) => { setBiz("mileageRate", e.target.value === "" ? "" : Number(e.target.value)); if (canIrs) setBiz("mileageMode", "custom"); }} type="number" step="0.001" className="v-mono w-24 rounded-lg px-3 py-2 text-sm" />
            <span className="text-xs text-ivory-dim">per</span>
            <div className="flex rounded-lg overflow-hidden" style={{ border: "1px solid var(--hair)" }}>
              {["mi", "km"].map((u) => <button key={u} onClick={() => setBiz("distanceUnit", u)} className="px-3 py-2 text-xs font-medium" style={{ background: unit === u ? "var(--brass)" : "var(--card2)", color: unit === u ? "var(--ink)" : "var(--ivory-dim)" }}>{u}</button>)}
            </div>
          </div>
          <div className="text-xs text-ivory-dim mt-2 leading-relaxed">{active.country === "CA" ? "In Canada, self-employed people deduct actual vehicle costs times their business-use share, not a per-km rate (the CRA's 73¢/67¢ rate is for employee allowances). Use this rate only as a rough stand-in." : active.country && !canIrs ? `There's no flat official mileage rate for the self-employed in ${COUNTRIES[active.country]?.name || "your country"}. Enter your own per-${unit} cost, or leave it at 0.` : "Set this to your tax authority's current business mileage rate. It changes most years."}</div>
        </>)}
      </div>


      {frequentTrips(trips).length > 0 && (
        <div className="mb-4">
          <div className="text-xs uppercase tracking-wide text-ivory-dim mb-2">Log again</div>
          <div className="flex flex-wrap gap-2">
            {frequentTrips(trips).map((t, i) => <button key={i} onClick={() => { setEditingId(null); setF({ date: localToday(), from: t.from || "", to: t.to || "", purpose: t.purpose || "", distance: String(t.distance), roundTrip: false, clientId: t.clientId || "" }); }} className="bg-card rounded-full px-3 py-1.5 text-xs">{t.from || "?"} → {t.to || "?"} · {t.distance} {unit}</button>)}
          </div>
        </div>)}

      <div className="bg-card2 rounded-2xl p-4 mb-5">
        <div className="text-xs uppercase tracking-wide text-ivory-dim mb-3">{editingId ? "Edit trip" : "Log a trip"}</div>
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <input value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} type="date" className="flex-1 rounded-lg px-3 py-2 text-sm" />
            <input value={f.distance} onChange={(e) => setF({ ...f, distance: e.target.value })} type="number" placeholder={`Distance (${unit})`} className="v-mono w-32 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="flex gap-2">
            <input value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} placeholder="From address" autoComplete="street-address" className="flex-1 min-w-0 rounded-lg px-3 py-2 text-sm" />
            <input value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} placeholder="To address" autoComplete="street-address" className="flex-1 min-w-0 rounded-lg px-3 py-2 text-sm" />
          </div>
          <button onClick={findDistance} disabled={look.busy || f.from.trim().length < 3 || f.to.trim().length < 3} className="w-full flex items-center justify-center gap-1.5 bg-card rounded-lg py-2 text-xs font-semibold text-brass disabled:opacity-40">{look.busy ? "Looking up the route…" : "Find driving distance from these addresses"}</button>
          {look.note && <div className="text-xs" style={{ color: "var(--sage)" }} role="status">{look.note}. Check it looks right, then add the trip.</div>}
          {look.err && <div className="text-xs" style={{ color: "var(--rust)" }} role="alert">{look.err}</div>}
          <input value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} placeholder="Business purpose (client visit, supplies run…)" className="w-full rounded-lg px-3 py-2 text-sm" />
          {active.clients.length > 0 && (
            <select value={f.clientId} onChange={(e) => setF({ ...f, clientId: e.target.value })} className="w-full rounded-lg px-3 py-2 text-sm" style={selStyle}>
              <option value="">No client</option>
              {active.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          {!editingId && <label className="flex items-center gap-2 text-xs text-ivory-dim"><input type="checkbox" checked={f.roundTrip} onChange={(e) => setF({ ...f, roundTrip: e.target.checked })} /> Round trip (count the distance twice)</label>}
          <div className="flex gap-2 mt-1">
            <button onClick={save} className="flex-1 bg-gold-grad text-ink font-semibold rounded-full py-2.5 text-sm">{editingId ? "Save trip" : "Add trip"}</button>
            {editingId && <button onClick={() => { setEditingId(null); setF(blank); }} className="px-4 text-xs text-ivory-dim">Cancel</button>}
          </div>
        </div>
      </div>

      {yearTrips.length === 0 ? <div className="text-center text-ivory-dim text-sm py-6">No trips logged for {year}.</div> : (
        <div className="flex flex-col gap-2">
          {yearTrips.map((t) => (
            <div key={t.id} className="bg-card rounded-xl p-3 flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm truncate">{t.from || t.to ? `${t.from || "?"} → ${t.to || "?"}` : (t.purpose || "Trip")}</div>
                <div className="text-xs text-ivory-dim truncate">{pretty(t.date)}{t.purpose && (t.from || t.to) ? ` · ${t.purpose}` : ""}{t.clientId ? ` · ${active.clients.find((c) => c.id === t.clientId)?.name || ""}` : ""}</div>
              </div>
              <div className="text-right shrink-0"><div className="v-mono text-sm">{t.distance} {unit}</div><div className="v-mono text-xs text-ivory-dim">{fmt(dedMap.get(t.id) || 0)}</div></div>
              <button onClick={() => startEdit(t)} className="opacity-60"><Pencil size={13} /></button>
              <button onClick={() => remove(t.id)} className="opacity-40"><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Staff hours
// ---------------------------------------------------------------------------
export function HoursView({ active, updateActive, setView, showToast, fmt }) {
  const staff = active.staff || [];
  const blank = { staffId: staff[0]?.id || "", date: localToday(), start: "", end: "", breakMin: "", hours: "", note: "", clientId: "" };
  const [f, setF] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [showStaff, setShowStaff] = useState(staff.length === 0);
  const [sName, setSName] = useState("");
  const [sPay, setSPay] = useState("");
  const [sBill, setSBill] = useState("");
  const [sEmail, setSEmail] = useState("");
  const [sOt, setSOt] = useState("");
  const [sTax, setSTax] = useState("");
  const [sEmp, setSEmp] = useState("");
  const [editStaffId, setEditStaffId] = useState(null);
  const hours = (active.hours || []).slice().sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  const today = localToday();
  const wk = weekStart(today);
  const monthPrefix = today.slice(0, 7);

  function clearStaffForm() { setSName(""); setSPay(""); setSBill(""); setSEmail(""); setSOt(""); setSTax(""); setSEmp(""); setEditStaffId(null); }
  function addStaff() {
    if (!sName.trim()) return;
    const data = { name: sName.trim(), payRate: num(sPay), billRate: sBill === "" ? null : num(sBill), email: sEmail.trim(), overtimeAfter: sOt === "" ? 0 : num(sOt), overtimeMult: 1.5, taxPct: sTax === "" ? 0 : num(sTax), employerCostPct: sEmp === "" ? 0 : num(sEmp) };
    if (editStaffId) updateActive((p) => ({ ...p, staff: (p.staff || []).map((s) => (s.id === editStaffId ? { ...s, ...data, overtimeMult: s.overtimeMult || 1.5 } : s)) }));
    else { const s = { id: uid(), ...data }; updateActive((p) => ({ ...p, staff: [...(p.staff || []), s] })); if (!f.staffId) setF((x) => ({ ...x, staffId: s.id })); }
    clearStaffForm();
  }
  function editStaff(s) { setEditStaffId(s.id); setSName(s.name); setSPay(String(s.payRate ?? "")); setSBill(s.billRate ? String(s.billRate) : ""); setSEmail(s.email || ""); setSOt(s.overtimeAfter ? String(s.overtimeAfter) : ""); setSTax(s.taxPct ? String(s.taxPct) : ""); setSEmp(s.employerCostPct ? String(s.employerCostPct) : ""); }
  function removeStaff(id) {
    if ((active.hours || []).some((h) => h.staffId === id)) { showToast("Delete their logged hours first"); return; }
    updateActive((p) => ({ ...p, staff: (p.staff || []).filter((s) => s.id !== id) }));
  }

  function onTime(k, v) {
    const next = { ...f, [k]: v };
    const h = hoursFromTimes(next.start, next.end, next.breakMin);
    if (h) next.hours = String(h);
    setF(next);
  }

  function save() {
    const h = num(f.hours);
    if (!f.staffId) { showToast("Add a staff member first"); setShowStaff(true); return; }
    if (h <= 0) { showToast("Enter the hours worked"); return; }
    const entry = { id: editingId || uid(), staffId: f.staffId, date: f.date, hours: h, start: f.start, end: f.end, note: f.note.trim(), clientId: f.clientId || null };
    updateActive((p) => ({ ...p, hours: editingId ? (p.hours || []).map((x) => (x.id === editingId ? { ...x, ...entry } : x)) : [entry, ...(p.hours || [])] }));
    setF({ ...blank, staffId: f.staffId, date: f.date }); setEditingId(null);
    showToast(editingId ? "Shift updated" : "Hours logged");
  }
  function startEdit(h) { setEditingId(h.id); setF({ staffId: h.staffId, date: h.date, start: h.start || "", end: h.end || "", breakMin: "", hours: String(h.hours), note: h.note || "", clientId: h.clientId || "" }); window.scrollTo?.({ top: 0, behavior: "smooth" }); }
  function remove(id) { updateActive((p) => ({ ...p, hours: (p.hours || []).filter((h) => h.id !== id) })); if (editingId === id) { setEditingId(null); setF(blank); } }

  function exportCSV() {
    const rows = [["Date", "Staff", "Start", "End", "Hours", "Pay rate", "Cost", "Client", "Note", "Billed on"]];
    hours.slice().reverse().forEach((h) => { const s = staff.find((x) => x.id === h.staffId); rows.push([h.date, s?.name || "", h.start || "", h.end || "", h.hours, s?.payRate || 0, (h.hours * (s?.payRate || 0)).toFixed(2), active.clients.find((c) => c.id === h.clientId)?.name || "", h.note || "", h.billedOn || ""]); });
    downloadText("staff-hours.csv", toCSV(rows));
  }

  const summary = staff.map((s) => {
    const mine = hours.filter((h) => h.staffId === s.id);
    const week = mine.filter((h) => h.date >= wk).reduce((t, h) => t + h.hours, 0);
    const month = mine.filter((h) => h.date.startsWith(monthPrefix)).reduce((t, h) => t + h.hours, 0);
    return { s, week, month, cost: month * (s.payRate || 0) };
  });
  const monthCost = laborCost(hours.filter((h) => h.date.startsWith(monthPrefix)), staff);

  return (
    <div className="px-5 pt-8 pb-28 wide-cols">
      <Header title="Staff hours" onBack={() => setView("business")} right={<button onClick={exportCSV} className="flex items-center gap-1 bg-card2 rounded-full px-3 py-1.5 text-xs font-medium"><Download size={12} /> CSV</button>} />

      {summary.length > 0 && (
        <div className="card-hero rounded-2xl p-4 mb-4">
          <div className="flex items-center justify-between mb-2"><div className="text-xs text-ivory-dim">This month's labor cost</div><div className="v-mono text-lg">{fmt(monthCost)}</div></div>
          <table className="w-full text-xs">
            <thead><tr className="text-ivory-dim text-left"><th className="font-normal pb-1">Staff</th><th className="font-normal pb-1 text-right">Week</th><th className="font-normal pb-1 text-right">Month</th><th className="font-normal pb-1 text-right">Cost</th></tr></thead>
            <tbody>{summary.map(({ s, week, month, cost }) => <tr key={s.id}><td className="py-1 truncate max-w-[110px]">{s.name}</td><td className="v-mono text-right">{week.toFixed(1)}h</td><td className="v-mono text-right">{month.toFixed(1)}h</td><td className="v-mono text-right">{fmt(cost)}</td></tr>)}</tbody>
          </table>
        </div>
      )}

      <div className="bg-card2 rounded-2xl p-4 mb-4">
        <div className="text-xs uppercase tracking-wide text-ivory-dim mb-3">{editingId ? "Edit shift" : "Log hours"}</div>
        {staff.length === 0 ? <div className="text-sm text-ivory-dim">Add a staff member below first.</div> : (
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <select value={f.staffId} onChange={(e) => setF({ ...f, staffId: e.target.value })} className="flex-1 rounded-lg px-3 py-2 text-sm" style={selStyle}>{staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
              <input value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} type="date" className="w-40 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="flex gap-2 items-center">
              <input value={f.start} onChange={(e) => onTime("start", e.target.value)} type="time" className="flex-1 rounded-lg px-2 py-2 text-sm" />
              <span className="text-xs text-ivory-dim">to</span>
              <input value={f.end} onChange={(e) => onTime("end", e.target.value)} type="time" className="flex-1 rounded-lg px-2 py-2 text-sm" />
              <input value={f.breakMin} onChange={(e) => onTime("breakMin", e.target.value)} type="number" placeholder="Break min" className="v-mono w-24 rounded-lg px-2 py-2 text-xs" />
            </div>
            <div className="flex gap-2 items-center">
              <input value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} type="number" step="0.25" placeholder="Hours" className="v-mono w-28 rounded-lg px-3 py-2 text-sm" />
              <span className="text-xs text-ivory-dim flex-1">Auto-filled from start/end, or type hours directly.</span>
            </div>
            <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Note (optional)" className="w-full rounded-lg px-3 py-2 text-sm" />
            {active.clients.length > 0 && (
              <select value={f.clientId} onChange={(e) => setF({ ...f, clientId: e.target.value })} className="w-full rounded-lg px-3 py-2 text-sm" style={selStyle}>
                <option value="">No client (internal)</option>
                {active.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
            <div className="text-xs text-ivory-dim">Tag a client to bill these hours from the invoice screen.</div>
            <div className="flex gap-2 mt-1">
              <button onClick={save} className="flex-1 bg-gold-grad text-ink font-semibold rounded-full py-2.5 text-sm">{editingId ? "Save shift" : "Add hours"}</button>
              {editingId && <button onClick={() => { setEditingId(null); setF(blank); }} className="px-4 text-xs text-ivory-dim">Cancel</button>}
            </div>
          </div>
        )}
      </div>

      <button onClick={() => setShowStaff((v) => !v)} className="flex items-center gap-1.5 text-xs text-brass font-medium mb-3">
        <ChevronDown size={13} style={{ transform: showStaff ? "rotate(180deg)" : "none" }} /> Manage staff
      </button>
      {showStaff && (
        <div className="bg-card rounded-2xl p-4 mb-5">
          {staff.map((s) => (
            <div key={s.id} className="flex items-center gap-3 py-2 border-b border-hair last:border-0">
              <div className="flex-1 min-w-0"><div className="text-sm truncate">{s.name}</div><div className="text-xs text-ivory-dim">Pay {fmt(s.payRate)}/h{s.billRate ? ` · Bill ${fmt(s.billRate)}/h` : ""}{s.overtimeAfter ? ` · OT after ${s.overtimeAfter}h/wk` : ""}{s.employerCostPct ? ` · employer +${s.employerCostPct}%` : ""}{s.email ? ` · ${s.email}` : ""}</div></div>
              <button onClick={() => editStaff(s)} className="opacity-60" aria-label={`Edit ${s.name}`}><Pencil size={13} /></button>
              <button onClick={() => removeStaff(s.id)} className="opacity-40" aria-label={`Remove ${s.name}`}><Trash2 size={13} /></button>
            </div>
          ))}
          <div className="flex flex-col gap-2 mt-3">
            <input value={sName} onChange={(e) => setSName(e.target.value)} placeholder="Name" className="w-full rounded-lg px-3 py-2 text-sm" />
            <div className="flex gap-2">
              <input value={sPay} onChange={(e) => setSPay(e.target.value)} type="number" placeholder="Pay / hour" className="v-mono flex-1 rounded-lg px-3 py-2 text-sm" />
              <input value={sBill} onChange={(e) => setSBill(e.target.value)} type="number" placeholder="Bill / hour (opt.)" className="v-mono flex-1 rounded-lg px-3 py-2 text-sm" />
            </div>
            <input value={sEmail} onChange={(e) => setSEmail(e.target.value)} type="email" placeholder="Email (to send payslips)" className="w-full rounded-lg px-3 py-2 text-sm" />
            <div className="flex gap-2">
              <input value={sOt} onChange={(e) => setSOt(e.target.value)} type="number" placeholder="Overtime after (h/week)" className="v-mono flex-1 rounded-lg px-3 py-2 text-sm" />
              <input value={sTax} onChange={(e) => setSTax(e.target.value)} type="number" placeholder="Default tax %" className="v-mono flex-1 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="flex gap-2 items-center">
              <input value={sEmp} onChange={(e) => setSEmp(e.target.value)} type="number" placeholder={payrollPlaceholder(active.country)} className="v-mono flex-1 rounded-lg px-3 py-2 text-sm" />
              {COUNTRIES[active.country]?.employerHint && <button onClick={() => setSEmp(String(COUNTRIES[active.country].employerHint.pct))} className="text-xs text-brass font-medium shrink-0">Use {COUNTRIES[active.country].employerHint.pct}%</button>}
            </div>
            {COUNTRIES[active.country]?.employerHint && <div className="text-xs text-ivory-dim">{COUNTRIES[active.country].employerHint.label}. This is extra cost for you; it isn't taken from the employee's pay.</div>}
            <div className="flex gap-2"><button onClick={addStaff} className="flex-1 bg-brass text-ink font-semibold rounded-lg py-2 text-sm">{editStaffId ? "Save changes" : "Add staff member"}</button>{editStaffId && <button onClick={clearStaffForm} className="px-3 text-xs text-ivory-dim">Cancel</button>}</div>
          </div>
        </div>
      )}

      {hours.length === 0 ? <div className="text-center text-ivory-dim text-sm py-6">No hours logged yet.</div> : (
        <div className="flex flex-col gap-2">
          {hours.slice(0, 100).map((h) => {
            const s = staff.find((x) => x.id === h.staffId);
            return (
              <div key={h.id} className="bg-card rounded-xl p-3 flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{s?.name || "Removed staff"}{h.clientId ? ` · ${active.clients.find((c) => c.id === h.clientId)?.name || ""}` : ""}</div>
                  <div className="text-xs text-ivory-dim truncate">{pretty(h.date)}{h.start && h.end ? ` · ${h.start}–${h.end}` : ""}{h.note ? ` · ${h.note}` : ""}{h.billedOn ? ` · billed ${h.billedOn}` : ""}{h.payslipId ? " · on payslip" : ""}</div>
                </div>
                <div className="text-right shrink-0"><div className="v-mono text-sm">{h.hours}h</div><div className="v-mono text-xs text-ivory-dim">{fmt(h.hours * (s?.payRate || 0))}</div></div>
                <button onClick={() => startEdit(h)} className="opacity-60"><Pencil size={13} /></button>
                <button onClick={() => remove(h.id)} className="opacity-40"><Trash2 size={13} /></button>
              </div>
            );
          })}
          {hours.length > 100 && <div className="text-xs text-ivory-dim text-center py-2">Showing the latest 100 — export CSV for everything.</div>}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recurring invoices
// ---------------------------------------------------------------------------
export function RecurringInvoicesView({ active, updateActive, setView, fmt, onNew }) {
  const list = active.recurringInvoices || [];
  const patch = (id, changes) => updateActive((p) => ({ ...p, recurringInvoices: (p.recurringInvoices || []).map((t) => (t.id === id ? { ...t, ...changes } : t)) }));
  const remove = (id) => updateActive((p) => ({ ...p, recurringInvoices: (p.recurringInvoices || []).filter((t) => t.id !== id) }));
  return (
    <div className="px-5 pt-8 pb-28 wide-cols">
      <Header title="Recurring invoices" onBack={() => setView("business")} />
      <p className="text-ivory-dim text-xs mb-5">A new draft invoice is created on each due date — including any you missed while the app was closed. You review and send them yourself.</p>
      <button onClick={onNew} className="w-full bg-gold-grad text-ink font-semibold rounded-full py-2.5 text-sm flex items-center justify-center gap-1.5 mb-6"><Plus size={15} /> New recurring invoice</button>

      {list.length === 0 ? <div className="text-center py-10 text-ivory-dim text-sm">Nothing repeating yet.<br />Retainers, rent you charge, monthly services — set one up once.</div> : (
        <div className="flex flex-col gap-2">
          {list.map((t) => {
            const client = active.clients.find((c) => c.id === t.clientId);
            const total = invoiceTotals(t).total;
            return (
              <div key={t.id} className="bg-card rounded-xl p-3.5" style={{ opacity: t.paused ? 0.6 : 1 }}>
                <div className="flex items-center justify-between mb-2">
                  <div className="min-w-0"><div className="text-sm font-semibold truncate">{client ? client.name : "No client"}</div><div className="text-xs text-ivory-dim truncate">{(t.items || []).map((i) => i.description).join(", ")}</div></div>
                  <div className="v-mono text-sm shrink-0">{fmt(total)}</div>
                </div>
                <div className="flex gap-2 items-center mb-2">
                  <select value={t.frequency} onChange={(e) => patch(t.id, { frequency: e.target.value })} className="rounded-md px-2 py-1 text-xs" style={selStyle}>{FREQUENCIES.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}</select>
                  <span className="text-xs text-ivory-dim">next</span>
                  <input type="date" value={t.nextDate} onChange={(e) => patch(t.id, { nextDate: e.target.value, anchorDay: Number(e.target.value.slice(8, 10)) })} className="rounded-md px-2 py-1 text-xs flex-1" />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => patch(t.id, { paused: !t.paused })} className="flex-1 flex items-center justify-center gap-1 bg-card2 rounded-lg py-1.5 text-xs font-medium">{t.paused ? <><Play size={12} /> Resume</> : <><Pause size={12} /> Pause</>}</button>
                  <label className="flex items-center gap-1.5 text-xs text-ivory-dim"><input type="checkbox" checked={!!t.autoSent} onChange={(e) => patch(t.id, { autoSent: e.target.checked })} /> Mark sent</label>
                  <button onClick={() => remove(t.id)} className="opacity-40 px-1"><Trash2 size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The printable invoice (white "paper" so it prints cleanly)
// ---------------------------------------------------------------------------
export function InvoicePaper({ inv, client, profile, fmt }) {
  const biz = profile.business || {};
  const t = invoiceTotals(inv);
  const name = biz.name || profile.name;
  const ink = "#1a1a1a", dim = "#6b6b6b", line = "#e4e4e4";
  const hasAct = (inv.items || []).some((it) => String(it.activity || "").trim());
  return (
    <div id="invoice-print-area" style={{ background: "#fff", color: ink, padding: 22, borderRadius: 10, boxShadow: "0 0 0 1px rgba(0,0,0,0.10)", fontFamily: "Inter, system-ui, sans-serif", fontSize: 12, lineHeight: 1.45 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 18 }}>
        <div style={{ minWidth: 0 }}>
          {biz.logo ? <img src={biz.logo} alt="" style={{ maxHeight: 56, maxWidth: 170, display: "block", marginBottom: 6, background: "#fff" }} /> : <div style={{ fontSize: 18, fontWeight: 700 }}>{name}</div>}
          {biz.logo && <div style={{ fontWeight: 600 }}>{name}</div>}
          {biz.address && <div style={{ color: dim, whiteSpace: "pre-line" }}>{biz.address}</div>}
          {(biz.email || biz.phone) && <div style={{ color: dim }}>{[biz.email, biz.phone].filter(Boolean).join(" · ")}</div>}
          {biz.website && <div style={{ color: dim }}>{biz.website}</div>}
          {biz.taxId && <div style={{ color: dim }}>{biz.taxLabel && biz.taxLabel !== "Tax" ? biz.taxLabel : "Tax ID"}: {biz.taxId}</div>}
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: 1.5 }}>INVOICE</div>
          <div style={{ color: dim }}>{inv.number}</div>
          {inv.status === "paid" && <div style={{ display: "inline-block", marginTop: 6, border: "1.5px solid #2f7d5b", color: "#2f7d5b", fontWeight: 700, padding: "1px 8px", borderRadius: 4, letterSpacing: 1 }}>PAID</div>}
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
        <div>
          <div style={{ color: dim, fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }}>Billed to</div>
          <div style={{ fontWeight: 600 }}>{client ? client.name : "—"}</div>
          {inv.billTo && <div style={{ color: dim, whiteSpace: "pre-line" }}>{inv.billTo}</div>}
        </div>
        <div style={{ textAlign: "right" }}>
          <div><span style={{ color: dim }}>Issued </span>{pretty(inv.issueDate)}</div>
          <div><span style={{ color: dim }}>Due </span>{pretty(inv.dueDate)}</div>
        </div>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 12 }}>
        <thead>
          <tr style={{ borderBottom: `1.5px solid ${ink}`, textAlign: "left", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: dim }}>
            {hasAct && <th style={{ padding: "4px 8px 4px 0", fontWeight: 600 }}>Activity</th>}<th style={{ padding: "4px 0", fontWeight: 600 }}>Description</th><th style={{ padding: "4px 6px", fontWeight: 600, textAlign: "right" }}>Qty</th><th style={{ padding: "4px 6px", fontWeight: 600, textAlign: "right" }}>Rate</th><th style={{ padding: "4px 0", fontWeight: 600, textAlign: "right" }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {inv.items.map((it) => (
            <tr key={it.id} style={{ borderBottom: `1px solid ${line}` }}>
              {hasAct && <td style={{ padding: "7px 8px 7px 0", verticalAlign: "top" }}>{it.activity}</td>}
              <td style={{ padding: "7px 0", verticalAlign: "top" }}>{it.description}</td>
              <td style={{ padding: "7px 6px", textAlign: "right", fontFamily: "Inter, system-ui, sans-serif", fontVariantNumeric: "tabular-nums" }}>{it.qty}</td>
              <td style={{ padding: "7px 6px", textAlign: "right", fontFamily: "Inter, system-ui, sans-serif", fontVariantNumeric: "tabular-nums" }}>{fmt(it.rate)}</td>
              <td style={{ padding: "7px 0", textAlign: "right", fontFamily: "Inter, system-ui, sans-serif", fontVariantNumeric: "tabular-nums" }}>{fmt(it.qty * it.rate)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ marginLeft: "auto", width: "62%", fontFamily: "Inter, system-ui, sans-serif", fontVariantNumeric: "tabular-nums" }}>
        {t.taxRate > 0 && (<>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}><span style={{ color: dim }}>Subtotal</span><span>{fmt(t.subtotal)}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}><span style={{ color: dim }}>{biz.taxLabel || "Tax"} ({t.taxRate}%)</span><span>{fmt(t.tax)}</span></div>
        </>)}
        <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderTop: `1.5px solid ${ink}`, marginTop: 3, fontSize: 15, fontWeight: 700 }}><span>Total</span><span>{fmt(t.total)}</span></div>
        {inv.status !== "paid" && invoicePaidAmount(inv) > 0 && invoiceBalance(inv) > 0 && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 12.5, color: dim }}><span>Paid to date</span><span>-{fmt(invoicePaidAmount(inv))}</span></div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 15, fontWeight: 700 }}><span>Balance due</span><span>{fmt(invoiceBalance(inv))}</span></div>
          </>
        )}
      </div>

      {(biz.paymentInfo || inv.notes) && (
        <div style={{ marginTop: 18, paddingTop: 12, borderTop: `1px solid ${line}` }}>
          {biz.paymentInfo && (<div style={{ marginBottom: 8 }}><div style={{ color: dim, fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }}>How to pay</div><div style={{ whiteSpace: "pre-line" }}>{biz.paymentInfo}</div></div>)}
          {inv.notes && <div style={{ color: dim, whiteSpace: "pre-line" }}>{inv.notes}</div>}
        </div>
      )}
    </div>
  );
}


